import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    EMPTY_ARRAY_LENGTH,
    LOCALE_LOCALIZATION_KEYS_MAP,
    PhoneNumberCustomValidator,
} from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { IUserGeneralInformation } from '../../../interfaces/user-general-information.interface';
import { ObjectUtils } from '../../../util/ObjectUtils';
import { BaseComponent } from '../../base.component';
@UntilDestroy()
@Component({
    selector: 'aumico-users-edit-general-information-component',
    templateUrl: 'general-information.component.html',
    styleUrls: ['general-information.component.scss'],
})
export class EditUserGeneralInformationComponent extends BaseComponent implements OnInit, OnChanges {
    private _user: IUserGeneralInformation | undefined;
    @Input() set user(value: IUserGeneralInformation) {
        if (value) {
            this._user = value;
            this.initFormGroup();
        }
    }
    get user(): IUserGeneralInformation | undefined {
        return this._user;
    }

    @Input() set supportedLocales(supportedLocalesValues: string[]) {
        this.setLanguages(supportedLocalesValues);
        this.initFormGroup();
    }

    @Input() countries: INameId[] = [];

    @Input() userCountry: string;

    @Output() userProfileGeneralInformationChange = new EventEmitter<IUserGeneralInformation>();

    profileFormGroup: UntypedFormGroup;
    salutations: INameId[] = [];
    languages: INameId[] = [];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private i18nextService: I18NextService,
    ) {
        super();
        this.applyLocalizations();
        this.addSvgIcon('aumico-users-icon-edit-image');
        this.profileFormGroup = this.formBuilder.group({
            salutation: new UntypedFormControl(),
            name: new UntypedFormControl(undefined, [Validators.required]),
            surname: new UntypedFormControl(undefined, [Validators.required]),
            email: new UntypedFormControl({ value: '', disabled: true }, []),
            phone: new UntypedFormControl(undefined, [PhoneNumberCustomValidator.phoneNumberValidator()]),
            language: new UntypedFormControl(),
            country: new FormControl<INameId[] | undefined>(undefined),
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['userCountry'] != null) {
            this.setCountry(this.userCountry);
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.profileFormGroup.valueChanges.subscribe((userGeneralInformation) => {
                const formRawValue = this.profileFormGroup.getRawValue();
                this.userProfileGeneralInformationChange.emit(this.mapUserGeneralInformationOutput(formRawValue));
            }),
        );
        this.setCountry(this.userCountry);
    }

    private initFormGroup(): void {
        if (this.languages.length === EMPTY_ARRAY_LENGTH || this._user == null) {
            return;
        }

        const generalInformation: IUserGeneralInformation = this._user || ({} as IUserGeneralInformation);
        const selectedSalutation = ObjectUtils.getSelectedItemFromDropDownOptions(
            generalInformation,
            'salutation',
            this.salutations,
        );
        const selectedLanguage = ObjectUtils.getSelectedItemFromDropDownOptions(
            generalInformation,
            'language',
            this.languages,
        );

        const country = this.getSelectedCountry();
        this.profileFormGroup.setValue({
            salutation: selectedSalutation || '',
            name: generalInformation.name,
            surname: generalInformation.surname,
            email: generalInformation.email,
            phone: generalInformation.phone,
            language: selectedLanguage || '',
            country,
        });
    }

    private applyLocalizations(): void {
        this.setSalutations();
    }

    private setLanguages(supportedLocales: string[]): void {
        this.languages = [];
        supportedLocales.forEach((locale) => {
            this.languages.push({
                id: locale,
                name: this.i18nextService.t(LOCALE_LOCALIZATION_KEYS_MAP.get(locale.toLocaleLowerCase())) as string,
            });
        });
    }

    private setSalutations(): void {
        this.salutations = [
            {
                id: 'mr',
                name: this.i18nextService.t('aumico_users_edit_user_general_information_salutations_mr') as string,
            },
            {
                id: 'mrs',
                name: this.i18nextService.t('aumico_users_edit_user_general_information_salutations_mrs') as string,
            },
        ];
    }

    private getSelectedCountry(): INameId | null {
        if (this.user == null) {
            throw new Error('The uses information in undefined');
        }

        if (this.user.country == null) {
            return null;
        }
        const selectedCountry = this.countries.find((country) => country.id === this.user.country.id);

        if (selectedCountry == null) {
            return null;
        }

        return selectedCountry;
    }

    updateProfilePicture(): void {
        // Not implemented yet!
        console.log('updateProfilePicture executed');
    }

    private mapUserGeneralInformationOutput(generalInformation: IUserGeneralInformation): IUserGeneralInformation {
        for (const key in generalInformation) {
            if (key === 'salutation' || key === 'language') {
                generalInformation[key] =
                    generalInformation[key] != '' && generalInformation[key] != null
                        ? generalInformation[key]['id'].toLocaleLowerCase()
                        : null;
            }
        }

        return generalInformation;
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private setCountry(countryToSet: string): void {
        if (countryToSet == null) {
            this.profileFormGroup.get('country').setValue(null);
            return;
        }

        const country = this.countries.find((country) => country.id === countryToSet);
        let selectCountry: INameId | null = null;

        if (country != null) {
            selectCountry = country;
        }
        this.profileFormGroup.get('country').setValue(selectCountry);
    }
}
